import React, { useState, useEffect, useRef, useCallback } from "react";
import { secureStorage } from "./utils/encryption";
import {
  Sun,
  ScissorsLineDashed,
  HelpCircle,
  MapPin,
  Warehouse,
  Truck,
  Send,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Info,
  CircleDollarSign,
  SunSnow,
  Shield,
  Shuffle,
  PaintBucket,
  StickyNote,
  ChevronUp,
  ChevronDown,
  Forklift,
} from "lucide-react";


const RadiativeCoolingQuote = () => {
  const topRef = useRef(null);
  const coverageInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isMetric, setIsMetric] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const [skipShipping, setSkipShipping] = useState(false);
  const [preservedShippingData, setPreservedShippingData] = useState({ shipping: "", receiving: "" });
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [exitIntentShown, setExitIntentShown] = useState(false);
  const [showMOQHelp, setShowMOQHelp] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [shippingBoxAnimating, setShippingBoxAnimating] = useState(false);
  const [formData, setFormData] = useState({
    coverage: "",
    product: "",
    subProduct: "",
    application: "",
    name: "",
    email: "",
    company: "",
    shipping: "",
    notes: "",
    receiving: "",
    mailingList: false, 
    skipShipping: false, 
  });

  // Progress saving utilities
  const STORAGE_KEY = 'coldrays-quote-progress';
  const STORAGE_EXPIRY_DAYS = 7;

  const saveProgress = useCallback(() => {
    try {
      const progressData = {
        formData,
        currentStep,
        isMetric,
        acceptedTerms,
        addressSelected,
        skipShipping,
        preservedShippingData,
        timestamp: Date.now()
      };
      
      // Use the expiration from your constant
      const expirationMinutes = STORAGE_EXPIRY_DAYS * 24 * 60;
      secureStorage.setItem(STORAGE_KEY, progressData, true, expirationMinutes);
    } catch (error) {
      console.warn('Failed to save progress:', error);
      // Optionally show user notification that auto-save failed
    }
  }, [formData, currentStep, isMetric, acceptedTerms, addressSelected, skipShipping, preservedShippingData]);

  const loadProgress = useCallback(() => {
    try {
      const progressData = secureStorage.getItem(STORAGE_KEY);
      if (!progressData) return;
      
      const { formData: savedFormData, ...rest } = progressData;
      
      // Load all state
      setCurrentStep(rest.currentStep || 1);
      setIsMetric(rest.isMetric || false);
      setAcceptedTerms(rest.acceptedTerms || false);
      setAddressSelected(rest.addressSelected || false);
      setSkipShipping(rest.skipShipping || false);
      setPreservedShippingData(rest.preservedShippingData || { shipping: "", receiving: "" });
      
      // Set form data with defaults for required fields
      if (savedFormData && typeof savedFormData === 'object') {
        setFormData(prev => ({
          ...prev,
          ...savedFormData,
          // Ensure required fields have defaults
          coverage: savedFormData.coverage || "",
          product: savedFormData.product || "",
          subProduct: savedFormData.subProduct || "",
          application: savedFormData.application || "",
          name: savedFormData.name || "",
          email: savedFormData.email || "",
          phone: savedFormData.phone || "",
          company: savedFormData.company || "",
          shipping: savedFormData.shipping || "",
          receiving: savedFormData.receiving || "",
          notes: savedFormData.notes || ""
        }));
      }
    } catch (error) {
      console.warn('Failed to load progress:', error);
      // Clear corrupted data
      secureStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const clearProgress = useCallback(() => {
    try {
      secureStorage.removeItem(STORAGE_KEY);
      // Reset form state to initial values
      setFormData({
        coverage: "",
        product: "",
        subProduct: "",
        application: "",
        name: "",
        email: "",
        phone: "",
        company: "",
        project: "",
        shipping: "",
        receiving: "",
        notes: "",
        mailingList: false
      });
      setCurrentStep(1);
      setAcceptedTerms(false);
      setAddressSelected(false);
      setSkipShipping(false);
      setShowNotes(false);
    } catch (error) {
      console.warn('Failed to clear progress:', error);
    }
  }, []);

  // Read URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const productParam = urlParams.get('product');
    
    if (productParam) {
      // Map SKU to product category and subProduct
      let productCategory = "";
      let subProduct = productParam;
      
      // Liquid Applied SKUs
      if (productParam === "W311" || productParam === "P311") {
        productCategory = "liquid-applied";
      }
      // Self-Adhesive SKUs
      else if (productParam === "FR1S300" || productParam === "FR5S300" || productParam === "FR5B100") {
        productCategory = "self-adhesive";
      }
      // Window Tint SKUs
      else if (productParam.startsWith("T") && productParam.includes("300W")) {
        productCategory = "window-tint";
      }
      // Fabric SKUs
      else if (productParam.startsWith("TP") || productParam.startsWith("HC")) {
        productCategory = "fabric";
      }
      
      if (productCategory) {
        setFormData(prev => ({
          ...prev,
          product: productCategory,
          subProduct: subProduct
        }));
      }
    }
  }, []);

  useEffect(() => {
    loadProgress();
    
    // Cleanup function to clear progress data on unmount
    return () => {
      try {
        // Only remove the actual storage key you're using
        secureStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn('Error during cleanup:', error);
      }
    };
  }, [loadProgress]);

  useEffect(() => {
    // Don't save if we've just submitted the form
    if (submitted) return;
    
    // Only save if we have meaningful data
    if (formData.coverage || formData.product || formData.name || formData.email) {
      const timer = setTimeout(() => {
        saveProgress();
      }, 1000); // Debounce for 1 second
  
      return () => clearTimeout(timer);
    }
  }, [formData, currentStep, isMetric, acceptedTerms, addressSelected, skipShipping, preservedShippingData, saveProgress]);

  // Handle smooth animation for large project shipping box
  useEffect(() => {
    if (!skipShipping && isLargeProject() && addressSelected) {
      // Animate in
      setShippingBoxAnimating(true);
      const timer = setTimeout(() => setShippingBoxAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [addressSelected, skipShipping, formData.product, formData.subProduct, formData.coverage,]);

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [shippingSuggestions, setShippingSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [coverageValid, setCoverageValid] = useState(true);
  const [coverageTooLarge, setCoverageTooLarge] = useState(false);

  const products = [
    {
      id: "liquid-applied",
      name: "Liquid Applied",
      icon: PaintBucket,
      desc: "Spray-on coating",
      specs: "Economical",
      specIcon: CircleDollarSign,
    },
    {
      id: "self-adhesive",
      name: "Self-Adhesive",
      icon: StickyNote,
      desc: "Peel & stick membrane",
      specs: "Premium",
      specIcon: SunSnow,
    },
    {
      id: "fabric",
      name: "Fabric",
      icon: ScissorsLineDashed,
      desc: "Coated polyester",
      specs: "Versatile",
      specIcon: Shuffle,
    },
    {
      id: "window-tint",
      name: "Window Tint",
      icon: Sun,
      desc: "Glass application",
      specs: "Protective",
      specIcon: Shield,
    },
    {
      id: "not-sure",
      name: "Need Guidance?",
      icon: HelpCircle,
      desc: "Product recommendation",
      specs: "",
      specIcon: HelpCircle,
    },
  ];

  const subProducts = {
    "liquid-applied": [
      {
        id: "P311",
        name: "Metal Coating",
        desc: "Advanced epoxy coating for metal surfaces",
        specs: "3-Layer Epoxy",
        color: "#FFFFFF",
      },
      {
        id: "W311",
        name: "Architectural Coating",
        desc: "Low-cost acrylic coating for diverse surfaces",
        specs: "4-Layer Acrylic",
        color: "#FFFFFF",
      },
    ],
    "self-adhesive": [
      {
        id: "FR5B100",
        name: "White Membrane",
        desc: "Bright white membrane for advanced weatherproofing on smooth and semi-smooth surfaces",
        specs: "Butyl-backed",
        color: "#FFFFFF",
      },
      {
        id: "FR5S300",
        name: "Silver Membrane",
        desc: "Diffuse silver membrane for advanced weatherproofing on smooth and semi-smooth surfaces",
        specs: "Butyl-backed",
        color: "#E8E8E8",
      },
      {
        id: "FR1S300",
        name: "Silver Film",
        desc: "Diffuse silver lightweight film with pressure-sensitive adhesive for smooth surfaces",
        specs: "PSA",
        color: "#E8E8E8",
      },
    ],
    "window-tint": [
      {
        id: "T13300W",
        name: "T13300W",
        desc: "11.7% transmittance / 63.6% reflectance",
        specs: "R63.6% / T11.7%",
        color: "#666666",
      },
      {
        id: "T26300W",
        name: "T26300W",
        desc: "26.5% transmittance / 25.4% reflectance",
        specs: "R25.4% / T26.5%",
        color: "#999999",
      },
      {
        id: "T36300W",
        name: "T36300W",
        desc: "36.6% transmittance / 20.0% reflectance",
        specs: "R20% / T36.6%",
        color: "#CCCCCC",
      },
    ],
    fabric: [
      {
        id: "TP300",
        name: "TP300",
        desc: "Taffeta",
        specs: "75D",
        color: "#FFFFFF",
      },
      {
        id: "TP400",
        name: "TP400",
        desc: "Lightweight Knit",
        specs: "30D",
        color: "linear-gradient(to left, #FFFFFF 0%, #FFFFFF 33%, #f2f8fc 33%, #f2f8fc 66%, #fffde6 66%, #fffde6 100%)",
      },
      {
        id: "TP500",
        name: "TP500",
        desc: "Oxford",
        specs: "150D",
        color: "#FFFFFF",
      },
      {
        id: "HC1-1",
        name: "HC1-1",
        desc: "Ripstop Blackout",
        specs: "63D",
        color: "linear-gradient(to right, #FFFFFF 50%, #FFFFFF 50%, #E8E8E8 50%, #E8E8E8 50%)",
      },
      {
        id: "HC1-2",
        name: "HC1-2",
        desc: "Oxford Blackout",
        specs: "150D",
        color: "linear-gradient(to right, #FFFFFF 50%, #FFFFFF 50%, #000000 50%, #000000 50%)",
      },
      {
        id: "HC1-3",
        name: "HC1-3",
        desc: "Ripstop Blackout",
        specs: "200D",
        color: "linear-gradient(to right, #FFFFFF 50%, #FFFFFF 50%, #000000 50%, #000000 50%)",
      },
      {
        id: "HC1-4",
        name: "HC1-4",
        desc: "Oxford Blackout",
        specs: "200D",
        color: "linear-gradient(to right, #FFFFFF 50%, #FFFFFF 50%, #000000 50%, #000000 50%)",
      },
      {
        id: "HC1-5",
        name: "HC1-5",
        desc: "Heavy Oxford",
        specs: "600D",
        color: "linear-gradient(to right, #FFFFFF 50%, #FFFFFF 50%, #e6d9c8 50%, #e6d9c8 50%)",
      },
    ],
  };

  const receivingOptions = [
    {
      id: "loading-dock",
      name: "Loading Dock Available",
      desc: "Standard truck delivery to loading dock",
      icon: Warehouse,
      suitable: "Large facilities, warehouses, commercial buildings",
    },
    {
      id: "lift-gate",
      name: "Lift Gate Required",
      desc: "Truck with hydraulic lift gate needed",
      icon: Truck,
      suitable: "Ground-level delivery, no loading dock",
    },
    {
      id: "special-handling",
      name: "Special Handling",
      desc: "Crane, forklift, or custom delivery method",
      icon: Forklift,
      suitable: "Remote locations, construction sites, special access",
    },
    {
      id: "skip-shipping",
      name: "Skip Shipping Details",
      desc: "Provide shipping information later",
      icon: HelpCircle,
      suitable: "Prefer to discuss shipping arrangements separately",
    },
  ];

  const mockShippingSuggestions = [
    "New York, NY, USA",
    "Los Angeles, CA, USA",
    "Chicago, IL, USA",
    "Houston, TX, USA",
    "Phoenix, AZ, USA",
    "Philadelphia, PA, USA",
  ];

  // Real shipping autocomplete using MapBox
  const fetchShippingSuggestions = async (query) => {
    const MAPBOX_TOKEN =
      "pk.eyJ1IjoiY2hyaXNjb2xkcmF5cyIsImEiOiJjbWM1OGhvZHIwcGZxMmxxNWdjd21zMG5sIn0.vsEpiDaPOd1Xl444Wd7bUw";

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${MAPBOX_TOKEN}&types=place,locality,address&limit=6`
      );
      const data = await response.json();

      return data.features.map((feature) => ({
        name: feature.place_name,
        coordinates: feature.center,
      }));
    } catch (error) {
      console.error("Geocoding error:", error);
      // Fallback to mock suggestions
      return mockShippingSuggestions
        .filter((city) => city.toLowerCase().includes(query.toLowerCase()))
        .map((city) => ({ name: city, coordinates: null }));
    }
  };

  // Unit conversion utilities
  const sqmToSqft = (sqm) => Math.round(sqm * 10.7639);
  const sqftToSqm = (sqft) => Math.round(sqft / 10.7639 * 100) / 100; // Round to 2 decimal places

  // Get coverage in square feet for internal calculations
  const getCoverageInSqft = () => {
    const coverage = parseInt(formData.coverage?.replace(/,/g, "") || 0);
    return isMetric ? sqmToSqft(coverage) : coverage;
  };

  // Get coverage in square meters for MOQ calculations
  const getCoverageInSqm = () => {
    const coverage = parseInt(formData.coverage?.replace(/,/g, "") || 0);
    return isMetric ? coverage : sqftToSqm(coverage);
  };

  // MOQ definitions in square meters
  const getMOQ = (productId, subProductId) => {
    if (productId === "liquid-applied") {
      if (subProductId === "P311") return 400; // Metal Coating
      if (subProductId === "W311") return 480; // Architectural Coating
    }
    if (productId === "self-adhesive") {
      if (subProductId === "FR1S300") return 61.25; // Silver Film
      if (subProductId === "FR5S300") return 30.1; // Silver Membrane
      if (subProductId === "FR5B100") return 30.1; // White Membrane
    }
    if (productId === "window-tint") {
      return 45.6; // All tints
    }
    if (productId === "fabric") {
      if (subProductId === "TP300") return 105;
      if (subProductId === "TP400") return 70;
      if (subProductId === "TP500") return 105;
      if (subProductId === "HC1-1") return 75;
      if (subProductId === "HC1-2") return 75;
      if (subProductId === "HC1-3") return 75;
      if (subProductId === "HC1-4") return 75;
      if (subProductId === "HC1-5") return 74;
    }
    return null;
  };

  // Check if coverage is below MOQ
  const isBelowMOQ = () => {
    if (!formData.product || !formData.subProduct || !formData.coverage) return false;
    const moq = getMOQ(formData.product, formData.subProduct);
    if (!moq) return false;
    const coverageInSqm = getCoverageInSqm();
    return coverageInSqm < moq;
  };

  // Get MOQ warning message
  const getMOQWarning = () => {
    if (!isBelowMOQ()) return null;
    const moq = getMOQ(formData.product, formData.subProduct);
    const coverageInSqm = getCoverageInSqm();
    const shortfall = moq - coverageInSqm;
    const productName = getSelectedSubProduct()?.name || formData.subProduct;
    
    // Check if product is film, membrane, or fabric for retail message
    const isRetailAvailable = formData.product === 'film' || formData.product === 'self-adhesive' || formData.product === 'fabric';
    const retailMessage = isRetailAvailable ? 'Sample quantities are available <a href="https://coldrays.com/product-category/retail/diy/" class="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">from our website</a>' : '';
    
    return {
      moq,
      shortfall,
      productName,
      message: `${productName} MOQ is ${formatNumber(sqmToSqft(moq))}ft² (${formatNumber(moq)}m²).`,
      retailMessage: retailMessage
    };
  };

  // Calculate units needed (MOQ is the unit size - roll/pail coverage)
  const getUnitsNeeded = () => {
    if (!formData.product || !formData.subProduct || !formData.coverage) return null;
    const unitSize = getMOQ(formData.product, formData.subProduct); // MOQ = size of one unit
    if (!unitSize) return null;
    
    const coverageInSqm = getCoverageInSqm();
    const exactUnits = coverageInSqm / unitSize;
    const unitsMin = Math.max(1, Math.floor(exactUnits)); // Never round down to 0
    const unitsMax = Math.ceil(exactUnits);
    const totalCoverageMin = unitsMin * unitSize;
    const totalCoverageMax = unitsMax * unitSize;
    
    // If min equals max, don't show range
    const isSameUnits = unitsMin === unitsMax;
    
    // Check if we're very close to a whole number (within 0.15 of min or max)
    const distanceToMin = exactUnits - unitsMin;
    const distanceToMax = unitsMax - exactUnits;
    const isVeryCloseToMin = distanceToMin <= 0.15 && unitsMin >= 1;
    const isVeryCloseToMax = distanceToMax <= 0.15;
    
    let recommendedUnits, recommendedCoverage, unitsDisplay;
    
    if (isSameUnits) {
      // Exact match or same min/max
      recommendedUnits = unitsMax;
      recommendedCoverage = totalCoverageMax;
      unitsDisplay = `${unitsMax}`;
    } else if (isVeryCloseToMin) {
      // Very close to minimum, recommend minimum
      recommendedUnits = unitsMin;
      recommendedCoverage = totalCoverageMin;
      unitsDisplay = `${unitsMin}`;
    } else if (isVeryCloseToMax) {
      // Very close to maximum, recommend maximum
      recommendedUnits = unitsMax;
      recommendedCoverage = totalCoverageMax;
      unitsDisplay = `${unitsMax}`;
    } else {
      // Show range when not close to either
      recommendedUnits = unitsMax; // Default to max for backward compatibility
      recommendedCoverage = totalCoverageMax;
      unitsDisplay = `${unitsMin}-${unitsMax}`;
    }
    
    return {
      unitsNeeded: unitsMax, // Keep for backward compatibility
      unitsMin,
      unitsMax,
      recommendedUnits,
      unitsDisplay,
      unitSize,
      totalCoverage: totalCoverageMax, // Keep for backward compatibility
      totalCoverageMin,
      totalCoverageMax,
      totalCoverageDisplay: (isSameUnits || isVeryCloseToMin || isVeryCloseToMax)
        ? (isMetric ? `${formatNumber(recommendedCoverage)}m²` : `${formatNumber(sqmToSqft(recommendedCoverage))}ft²`)
        : (isMetric 
          ? `${formatNumber(totalCoverageMin)}-${formatNumber(totalCoverageMax)}m²`
          : `${formatNumber(sqmToSqft(totalCoverageMin))}-${formatNumber(sqmToSqft(totalCoverageMax))}ft²`),
      unitSizeDisplay: isMetric 
        ? `${formatNumber(unitSize)}m²`
        : `${formatNumber(sqmToSqft(unitSize))}ft²`
    };
  };

  // Format display units
  const getUnitLabel = () => {
    return isMetric ? "m²" : "ft²";
  };

  // Get appropriate unit term based on product type
  const getUnitTerm = (count = 1) => {
    if (!formData.product) return count === 1 ? 'unit' : 'units';
    
    switch (formData.product) {
      case 'fabric':
      case 'self-adhesive':
      case 'window-tint':
        return count === 1 ? 'roll' : 'rolls';
      default:
        return count === 1 ? 'unit' : 'units';
    }
  };

  // Calculate drum requirements for liquid-applied coatings (internal use)
  const getDrumRequirements = () => {
    if (formData.product !== 'liquid-applied' || !formData.subProduct || !formData.coverage) {
      return null;
    }

    const coverageInSqm = getCoverageInSqm();
    if (!coverageInSqm) return null;

    let drumRequirements = {};

    if (formData.subProduct === 'P311') {
      // Metal coating (sprayed) coverage rates per drum
      const primerCoverage = 80; // m² per drum
      const midcoatCoverage = 50; // m² per drum  
      const topcoatCoverage = 80; // m² per drum

      drumRequirements = {
        productType: 'Metal Coating (P311)',
        primer: Math.ceil(coverageInSqm / primerCoverage),
        midcoat: Math.ceil(coverageInSqm / midcoatCoverage),
        topcoat: Math.ceil(coverageInSqm / topcoatCoverage),
        totalDrums: Math.ceil(coverageInSqm / primerCoverage) + 
                   Math.ceil(coverageInSqm / midcoatCoverage) + 
                   Math.ceil(coverageInSqm / topcoatCoverage)
      };
    } else if (formData.subProduct === 'W311') {
      // Architectural coating (sprayed) coverage rates per drum
      const primerCoverage = 160; // m² per drum
      const midcoatCoverage = 40; // m² per drum
      const topcoatCoverage = 120; // m² per drum
      const protectiveCoverage = 160; // m² per drum

      drumRequirements = {
        productType: 'Architectural Coating (W311)',
        primer: Math.ceil(coverageInSqm / primerCoverage),
        midcoat: Math.ceil(coverageInSqm / midcoatCoverage),
        topcoat: Math.ceil(coverageInSqm / topcoatCoverage),
        protective: Math.ceil(coverageInSqm / protectiveCoverage),
        totalDrums: Math.ceil(coverageInSqm / primerCoverage) + 
                   Math.ceil(coverageInSqm / midcoatCoverage) + 
                   Math.ceil(coverageInSqm / topcoatCoverage) + 
                   Math.ceil(coverageInSqm / protectiveCoverage)
      };
    }

    return drumRequirements;
  };

  // Check if large project requires shipping options (product-specific thresholds)
  const isLargeProject = () => {
    const coverageInSqft = getCoverageInSqft();

    if (formData.product === "liquid-applied") {
      return coverageInSqft >= 4000;
    }
    if (formData.product === "self-adhesive") {
      if (
        formData.subProduct === "FR1S300" ||
        formData.subProduct === "FR5B100"
      ) {
        return coverageInSqft >= 3000; // Film is lighter
      }
      if (formData.subProduct === "FR5S300") {
        return coverageInSqft >= 1500; // Membrane is heavier
      }
      return coverageInSqft >= 2000; // Default if no sub-product selected yet
    }
    if (formData.product === "window-tint") {
      return coverageInSqft >= 3000;
    }
    if (formData.product === "fabric") {
      return coverageInSqft >= 4000;
    }

    return false; // "not-sure" or no product selected
  };

  // Get the threshold message for display
  const getShippingThresholdMessage = () => {
    if (formData.product === "liquid-applied") {
      return "Projects over 4,000 ft² (372 m²) require special shipping considerations.";
    }
    if (formData.product === "self-adhesive") {
      if (
        formData.subProduct === "FR1S300" ||
        formData.subProduct === "FR5B100"
      ) {
        return "Projects over 3,000 ft² (279 m²) require special shipping considerations.";
      }
      if (formData.subProduct === "FR5S300") {
        return "Projects over 1,500 ft² (139 m²) require special shipping considerations.";
      }
      return "Large self-adhesive projects require special shipping considerations.";
    }
    if (formData.product === "window-tint") {
      return "Projects over 3,000 ft² (279 m²) require special shipping considerations.";
    }
    if (formData.product === "fabric") {
      return "Projects over 4,000 ft² (372 m²) require special shipping considerations.";
    }
    return "Large projects require special shipping considerations.";
  };

  // Helper function to get missing fields for step 1
  const getStep1MissingFields = () => {
    const missing = [];
    if (!formData.coverage) missing.push("Coverage area");
    if (!formData.product) missing.push("Product selection");
    if (formData.product === "not-sure" && !formData.application) {
      missing.push("Application details");
    } else if (formData.product && formData.product !== "not-sure" && !formData.subProduct) {
      missing.push("Specific product");
    }
    return missing;
  };

  // Helper function to get missing fields for step 2
  const getStep2MissingFields = () => {
    const missing = [];
    if (!formData.name) missing.push("Name");
    if (!formData.email) missing.push("Email");
    if (!emailValid) missing.push("Valid email");
    if (!skipShipping && !formData.shipping) missing.push("Shipping address");
    if (!formData.coverage) missing.push("Coverage area");
    if (!coverageValid) {
      if (coverageTooLarge) {
        missing.push("Coverage area under 1,000,000 sqft");
      } else {
        missing.push("Valid coverage area");
      }
    }
    if (!acceptedTerms) missing.push("Terms acceptance");
    if (!skipShipping && isLargeProject() && !formData.receiving) missing.push("Receiving method");
    return missing;
  };

  // Format numbers with commas
  const formatNumber = (num) => {
    if (!num || num === "") return "";
    
    // Remove any existing commas and convert to string
    const cleanNum = num.toString().replace(/,/g, "");
    const parsed = parseInt(cleanNum);
    
    // Check if it's a valid number
    if (isNaN(parsed)) return "";
    
    // Cap at a reasonable maximum (5 million square feet covers even the largest buildings)
    const maxValue = 5000001; // 5 million
    const cappedValue = Math.min(parsed, maxValue - 1);
    
    // Use toLocaleString for comma formatting
    return cappedValue.toLocaleString();
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Coverage validation
  const validateCoverage = (coverage) => {
    if (!coverage || coverage === "") return { valid: true, tooLarge: false }; // Empty is valid
    const numericValue = parseInt(coverage.replace(/,/g, ""));
    
    // Check for valid number and reasonable range
    if (isNaN(numericValue)) return { valid: false, tooLarge: false };
    if (numericValue < 0) return { valid: false, tooLarge: false };
    if (numericValue > 1000000) return { valid: false, tooLarge: true }; // 1 million max
    
    return { valid: true, tooLarge: false };
  };

  // Handle coverage input with comma formatting while preserving cursor position
  const handleCoverageChange = (e) => {
    const input = e.target;
    const value = input.value;
    const cursorPosition = input.selectionStart;
    
    // Count commas before cursor position to adjust for formatting changes
    const commasBefore = (value.substring(0, cursorPosition).match(/,/g) || []).length;
    
    // Remove all non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, "");
    const validation = validateCoverage(numericValue);
    setCoverageValid(validation.valid);
    setCoverageTooLarge(validation.tooLarge);
    
    // Only format if there's a value, otherwise keep empty
    if (numericValue === "") {
      setFormData((prev) => ({ ...prev, coverage: "" }));
    } else {
      const formatted = formatNumber(numericValue);
      setFormData((prev) => ({ ...prev, coverage: formatted }));
      
      // Restore cursor position after formatting
      setTimeout(() => {
        if (coverageInputRef.current) {
          const newCommasBefore = (formatted.substring(0, cursorPosition).match(/,/g) || []).length;
          const adjustment = newCommasBefore - commasBefore;
          const newPosition = Math.min(cursorPosition + adjustment, formatted.length);
          coverageInputRef.current.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
    }
  };

  // Handle email input with validation
  const handleEmailChange = (value) => {
    const isValid = value === "" || validateEmail(value);
    setEmailValid(isValid);
    setFormData((prev) => ({ ...prev, email: value }));
  };

  // Handle shipping input with suggestions
  const handleShippingChange = async (value) => {
    setFormData((prev) => ({ ...prev, shipping: value }));

    // Reset address selected state if value is cleared or too short
    if (!value || value.length < 10) {
      setAddressSelected(false);
    }

    if (value.length > 2) {
      setShowSuggestions(true);

      // Use real MapBox API
      try {
        const suggestions = await fetchShippingSuggestions(value);
        setShippingSuggestions(suggestions);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        // Fallback to mock suggestions
        const filtered = mockShippingSuggestions
          .filter((city) => city.toLowerCase().includes(value.toLowerCase()))
          .map((city) => ({ name: city, coordinates: null }));
        setShippingSuggestions(filtered);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleNext = () => {
    if (
      formData.coverage &&
      formData.product &&
      (formData.product === "not-sure"
        ? formData.application
        : formData.subProduct)
    ) {
      saveProgress(); // Save progress before step transition
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(2);
        setIsTransitioning(false);
        // Scroll to top when moving to next step - use setTimeout to ensure DOM updates
        setTimeout(() => {
          if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 50);
      }, 200);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null); // Clear any previous errors when attempting to submit

    try {
      // Get user metadata
      const getUserMetadata = async () => {
        const metadata = {
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          windowSize: `${window.innerWidth}x${window.innerHeight}`,
          referrer: document.referrer || 'direct',
          timestamp: new Date().toISOString(),
        };
        return metadata;
      };

      const userMetadata = await getUserMetadata();

      // Prepare the form data for submission
      const submitData = {
        ...formData,
        isMetric,
        coverageInSqft: getCoverageInSqft(),
        coverageInSqm: getCoverageInSqm(),
        units: getUnitLabel(),
        isLargeProject: isLargeProject(),
        selectedProduct: getSelectedProduct(),
        selectedSubProduct: getSelectedSubProduct(),
        drumRequirements: getDrumRequirements(),
        unitsNeeded: getUnitsNeeded(),
        moqStatus: isBelowMOQ() ? "Below MOQ" : "Meets MOQ",
        moqWarning: getMOQWarning(),
        timestamp: new Date().toISOString(),
        metadata: userMetadata
      };

      // Submit to Cloudflare Worker
      const response = await fetch("https://quote-handler.chris-d96.workers.dev", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setTimeout(() => {
          clearProgress();
          setSubmitted(true);
          setSubmitError(null);
        }, 100);
        
        // Scroll to top when showing success page
        setTimeout(() => {
          if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 50);
      } else {
        throw new Error(result.error || "Failed to submit quote request");
      }
    } catch (error) {
      console.error("Error submitting quote:", error);
      setSubmitError({
        title: "Submission Failed",
        message: "There was an error submitting your quote request. Please try again or contact us directly.",
        details: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate progress for temperature gauge
  const getProgressPercentage = () => {
    let progress = 0;
    if (formData.coverage) progress += 20;
    if (formData.product) progress += 30;
    if (formData.product !== "not-sure" && formData.subProduct) progress += 20;
    if (formData.product === "not-sure" && formData.application) progress += 20;
    if (currentStep === 2) progress += 10;
    if (formData.name && formData.email && formData.shipping) progress += 20;
    return Math.min(progress, 100);
  };

  const progressPercentage = getProgressPercentage();

  const getSelectedProduct = () => {
    return products.find((p) => p.id === formData.product);
  };

  const getSelectedSubProduct = () => {
    if (!formData.product || !formData.subProduct) return null;
    return subProducts[formData.product]?.find(
      (sp) => sp.id === formData.subProduct
    );
  };

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      // Only trigger if mouse leaves from the top of the page and form has some data, and hasn't been shown before
      if (e.clientY <= 0 && (formData.coverage || formData.product || formData.name || formData.email) && !showExitIntent && !exitIntentShown && currentStep < 3) {
        saveProgress(); // Save progress when user tries to leave
        setShowExitIntent(true);
        setExitIntentShown(true);
        // Auto-hide after 3 seconds to avoid being annoying
        setTimeout(() => setShowExitIntent(false), 3000);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [formData, showExitIntent, exitIntentShown, currentStep]);

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center md:p-6 p-2">
        <div className="bg-white border border-gray-200 shadow-lg md:p-6 p-3 max-w-2xl w-full text-center">
          <div className="relative mb-4">
            <div className="w-24 h-24 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-2 animate-pulse">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl md:p-6 p-3 md:mb-8 mb-4">
            <h3 className="font-semibold text-gray-900 mb-2 text-center">
              Your Quote Journey
            </h3>
            
            <div className="relative text-left">
              {/* Timeline line */}
              <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-blue-800 via-blue-100 to-blue-50"></div>
              
              <div className="space-y-6">
                {/* Step 1 - Current */}
                <div className="relative flex items-start space-x-4">
                  <div className="relative z-10 w-12 h-12 bg-blue-800 rounded-full flex items-center justify-center shadow-lg">
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex-1 pt-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-blue-900">Analysis</h4>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-900">
                        In Progress
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Our team is reviewing your product, coverage, and shipping requirements
                    </p>
                  </div>
                </div>

                {/* Step 2 - Next */}
                <div className="relative flex items-start space-x-4">
                  <div className="relative z-10 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-gray-400 font-bold text-sm">2</span>
                  </div>
                  <div className="flex-1 pt-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-gray-900">Proposal</h4>
                      <span className="text-xs text-gray-500 font-medium">1-2 Business Days</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Receive a detailed quote with pricing, shipping, and installation info
                    </p>
                  </div>
                </div>

                {/* Step 3 - Final */}
                <div className="relative flex items-start space-x-4">
                  <div className="relative z-10 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-400 font-bold text-sm">3</span>
                  </div>
                  <div className="flex-1 pt-2">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-gray-900">Planning</h4>
                      <span className="text-xs text-gray-500 font-medium">Follow-Up</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Finalize timeline, delivery logistics, and purchase order
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          
          
          <div className="mb-6">
            <p className="text-gray-600 mb-2">
              While you wait, learn more about the science behind radiative
              cooling:
            </p>
            <a
              href="https://coldrays.com/the-science-of-passive-daytime-radiative-cooling/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105"
            >
              <span>Read: How Radiative Cooling Works</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                setSubmitted(false);
                setCurrentStep(1);
                setAcceptedTerms(false);
                setFormData({
                  coverage: "",
                  product: "",
                  subProduct: "",
                  application: "",
                  name: "",
                  email: "",
                  company: "",
                  shipping: "",
                  notes: "",
                  receiving: "",
                  mailingList: false,
                });
              }}
              className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium transition-all duration-300 transform hover:scale-105"
            >
              Submit Another Quote
            </button>
            <a
              href="https://coldrays.com"
              className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium text-cente transition-all duration-300 transform hover:scale-105"
            >
              Return to Homepage
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Exit Intent Message */}
      {showExitIntent && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm">
          <div className="flex items-center space-x-2">
            <Info className="w-4 h-4" />
            <span>Your progress is automatically saved</span>
            <button 
              onClick={() => setShowExitIntent(false)}
              className="ml-2 text-blue-200 hover:text-white"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div ref={topRef} className="max-w-screen-2xl mx-auto px-2 sm:px-6 pt-2 pb-8 sm:pt-6">
        {/* Desktop Step Indicator */}
        <div className="hidden sm:flex items-center space-x-4 mb-8">
          <div
            className={`flex items-center space-x-2 ${
              currentStep >= 1 ? "text-blue-600" : "text-gray-400"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium ${
                currentStep >= 1
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-gray-300"
              }`}
            >
              1
            </div>
            <span className="font-medium">Product Configuration</span>
          </div>
          <div
            className={`w-12 h-px ${
              currentStep >= 2 ? "bg-blue-600" : "bg-gray-300"
            }`}
          ></div>
          <div
            className={`flex items-center space-x-2 ${
              currentStep >= 2 ? "text-blue-600" : "text-gray-400"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium ${
                currentStep >= 2
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-gray-300"
              }`}
            >
              2
            </div>
            <span className="font-medium">Contact & Shipping</span>
          </div>
        </div>

        {/* Mobile Compact Progress Indicator */}
        <div className="sm:hidden">
          <div className="bg-white border border-gray-200 rounded-t-xl px-3 py-2 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  currentStep === 1 ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"
                }`}>
                  {currentStep === 1 ? "1" : "✓"}
                </div>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  currentStep === 2 ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"
                }`}>
                  2
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {currentStep === 1 ? "Product Configuration" : "Contact & Shipping"}
                </div>
                <div className="text-xs text-gray-500">
                  Step {currentStep} of 2
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Quote Summary */}
        <div className="lg:hidden mb-2">
          <div className="bg-white border border-gray-200 rounded-b-xl shadow-sm overflow-hidden">
            <div
              className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex flex-grow items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">$</span>
                </div>
                <div className="flex-1">
                  <h2 className="font-medium text-sm text-gray-900">Quote Summary</h2>
                  <p className="text-xs text-gray-500 mb-1">
                    {formData.product && formData.coverage 
                      ? (() => {
                          const baseInfo = `${formatNumber(formData.coverage)} ${getUnitLabel()} • ${getSelectedProduct()?.name || "—"}`;
                          const subProduct = getSelectedSubProduct()?.name || "";
                          
                          // Add rolls/coverage info for non-liquid-applied products
                          if (formData.product !== 'liquid-applied' && getUnitsNeeded()) {
                            const units = getUnitsNeeded();
                            const rollsInfo = `${units.unitsDisplay} ${getUnitTerm(units.recommendedUnits || 1)}`;
                            return `${baseInfo} • ${subProduct} • ${rollsInfo}`;
                          }
                          
                          return `${baseInfo} • ${subProduct}`;
                        })()
                      : "Get started"
                    }
                  </p>
                  <div className="flex items-center space-x-2 -mt-1">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-1.5 bg-gradient-to-r from-orange-400 via-yellow-400 to-blue-400 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {progressPercentage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {currentStep === 2 && (
      <div className="p-0 space-y-0">
        {/* MOQ Warning for Mobile */}
        {isBelowMOQ() && (
          <div className="p-2 bg-orange-50 border-l-4 border-orange-200 text-xs">
            <div className="flex items-start space-x-1">
              <div className="flex-1">
                <div className="flex items-center justify-between">                 
                  <p className="font-medium text-orange-800">
                  ⓘ Below Minimum Order Quantity
                  </p>
                  <button
                    onClick={() => setShowMOQHelp(!showMOQHelp)}
                    className="flex items-center space-x-1 text-xs bg-orange-50 hover:bg-orange-200 text-orange-700 px-2 py-0.5 rounded transition-colors"
                  >
                    <span>{showMOQHelp ? 'Hide' : 'Info'}</span>
                    {showMOQHelp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                </div>
                <div className="text-orange-700 leading-relaxed">
                  {getMOQWarning()?.message}
                </div>
                {getMOQWarning()?.retailMessage && (
                  <div className="text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{__html: getMOQWarning()?.retailMessage}}>
                  </div>
                )}
                {showMOQHelp && (
                  <div className="text-gray-600 text-xs leading-relaxed animate-fadeIn">
                    <strong>Note:</strong> Quotes below MOQ may not be able to be fulfilled through our standard wholesale process, but we're still happy to review your request and explore custom solutions or retail options.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="p-2 bg-amber-50 border-l-4 border-amber-200 text-xs rounded-b-lg">
                
                  <div className="text-gray-600 leading-relaxed">
                    Quoted coverage assumes smooth, flat surface.
                    Actual requirements may vary due to{" "}
                    {formData.product === "liquid-applied" &&
                      "installation method (sprayed or rolled), surface texture, surface shape, and waste factor"}
                    {formData.product === "fabric" &&
                      "seams and material layout"}
                    {formData.product === "self-adhesive" &&
                      "surface shape and waste factor"}
                    {formData.product === "window-tint" &&
                      "cutting requirements"}
                    {formData.product === "not-sure" &&
                      "installation method and surface conditions"}
                    . Add{" "}
                    {formData.product === "liquid-applied" ||
                    formData.product === "self-adhesive"
                      ? "10% for simple installations, 25% for complex shapes like corrugated roofing"
                      : "10% for waste and installation variables"}
                    .
                    {formData.product === "self-adhesive" && " Depending on product layout, adjacent pieces may require overlap of up to 8%."}
                  </div>
                </div>
      </div>
    )}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <div className="bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl shadow-sm">
              {currentStep === 1 && (
                <div className={`p-4 sm:p-6 ${isTransitioning ? 'animate-slideOut' : 'animate-slideIn'}`}>
                  <h2 className="text-lg font-medium text-gray-900 mb-1">
                    Configure Your Solution
                  </h2>

                  <div className="mb-0">
                    <div className="flex items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Desired Coverage <span className="text-gray-400">(Estimated)</span>
                      </label>
                      {formData.coverage && coverageValid && (
                        <CheckCircle className="w-4 h-4 text-teal-500 ml-2" />
                      )}
                    </div>
                    <div className="relative max-w-xs">
                      <div className="relative">
                        <input
                          ref={coverageInputRef}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoFocus={window.innerWidth >= 768 && currentStep === 1}
                          value={formData.coverage}
                          onChange={handleCoverageChange}
                          className={`w-full px-3 py-3 text-lg border focus:outline-none transition-colors pr-16 rounded-lg ${
                            !coverageValid
                              ? "border-red-500 focus:border-red-500 bg-red-50"
                              : formData.coverage && coverageValid
                              ? "border-teal-500 focus:border-blue-500"
                              : "border-gray-300 focus:border-blue-500"
                          }`}
                          placeholder="10,000"
                          required
                        />
                        <div className="absolute right-1 top-1/2 transform -translate-y-1/2">
                          <select
                            value={isMetric ? "metric" : "imperial"}
                            onChange={(e) =>
                              setIsMetric(e.target.value === "metric")
                            }
                            className="bg-transparent border-0 text-sm text-gray-700 focus:outline-none cursor-pointer pr-6 py-2"
                          >
                            <option value="imperial">ft²</option>
                            <option value="metric">m²</option>
                          </select>
                        </div>
                      </div>
                      {/* Reserve space for error message to prevent layout shift */}
                      <div className="h-6 mt-1">
                        {!coverageValid && (
                          <p className="text-red-500 text-sm">
                            {coverageTooLarge ? "Maximum coverage" : "Please enter a valid number"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="lg:grid lg:grid-cols-2 lg:gap-8 space-y-4 lg:space-y-0">
                    {/* Product Type Selection */}
                    <div className="p-3 sm:p-4 border h-fit rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        Select Product Type
                      </label>
                      <div className="space-y-2">
                        {products.map((product) => {
                          const Icon = product.icon;
                          const SpecIcon = product.specIcon;
                          return (
                            <label key={product.id} className="cursor-pointer">
                              <input
                                type="radio"
                                name="product"
                                value={product.id}
                                checked={formData.product === product.id}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    product: e.target.value,
                                    subProduct: "", // Clear sub-product when main product changes
                                  }));
                                  
                                  // Auto-scroll down on mobile after product selection
                                  if (window.innerWidth <= 768) {
                                    setTimeout(() => {
                                      window.scrollBy({ top: 800, behavior: 'smooth' });
                                    }, 50);
                                  }
                                }}
                                className="sr-only"
                              />
                              <div
                                className={`p-2 sm:p-3 mb-1 border transition-colors flex items-center w-full ${
                                  formData.product === product.id
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-300 hover:border-gray-400"
                                } rounded-lg`}
                              >
                                <div className="flex items-center space-x-4 w-full">
                                  <div
                                    className={`p-2.5 ${
                                      formData.product === product.id
                                        ? "bg-blue-500"
                                        : "bg-gray-100"
                                    }`}
                                  >
                                    <Icon
                                      className={`w-6 h-6 ${
                                        formData.product === product.id
                                          ? "text-white"
                                          : "text-gray-600"
                                      }`}
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <h3 className="font-medium text-gray-900">
                                          {product.name}
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                          {product.desc}
                                        </p>
                                      </div>
                                      <div className="flex items-center space-x-1 text-sm text-gray-500 text-right">
                                      <SpecIcon className="w-3.5 h-3.5" />
                                        <span className="align-right">{product.specs}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Sub-Product Selection */}
                    <div className="p-3 sm:p-4 bg-gray-50 border border-gray-200 h-fit rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        {formData.product === "not-sure" ? "Application Details" : "Select Specific Product"}
                      </label>
                      {!formData.product ? (
                        <div className="p-6 text-center text-gray-500 border-1 border-gray-300 rounded">
                          Select a product type to see specific options
                        </div>
                      ) : formData.product === "not-sure" ? (
                        <div />
                      ) : (
                        <div className={`${formData.product === 'fabric' ? 'grid grid-cols-1 xl:grid-cols-2 gap-x-1 gap-y-1' : 'space-y-1'}`}>
                          {subProducts[formData.product].map((subProduct) => (
                            <label key={subProduct.id} className="cursor-pointer">
                              <input
                                type="radio"
                                name="subProduct"
                                value={subProduct.id}
                                checked={formData.subProduct === subProduct.id}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    subProduct: e.target.value,
                                  }))
                                }
                                className="sr-only"
                              />
                              <div
                                className={`p-2 mb-1 border transition-colors h-full flex items-center w-full ${
                                  formData.subProduct === subProduct.id
                                    ? "border-blue-400 bg-blue-50"
                                    : "border-gray-200 hover:border-gray-300 bg-white"
                                } rounded-lg`}
                              >
                                <div className="flex items-center space-x-4">
                                  <div className="w-12 h-12 bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                                    <div
                                      className="w-9 h-9 border border-gray-300"
                                      style={{
                                        background: subProduct.color,
                                      }}
                                    ></div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <h4 className="font-medium text-gray-900 text-sm">
                                        {subProduct.name}
                                      </h4>
                                      {(formData.product === "self-adhesive" || formData.product === "liquid-applied" || formData.product === "fabric") && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                          {subProduct.specs}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {subProduct.desc}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                      {formData.product === "not-sure" && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                              <div>
                                <p className="text-sm text-gray-600">
                              Help us understand your project so we can recommend the most suitable products from our range.
                              </p>
                              </div>
                            </div>
                          </div>
                          <textarea
                            value={formData.application}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                application: e.target.value,
                              }))
                            }
                            className="w-full px-4 py-3 border border-gray-300 focus:border-blue-500 focus:outline-none transition-colors resize-none rounded-lg"
                            rows={5}
                            placeholder="Describe your cooling needs and surface materials and conditions..."
                            required
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="relative mt-8 pb-8 sm:pb-2">
                    <button
                    onMouseEnter={() =>  {
                      const missingFields = getStep1MissingFields();
                      if (missingFields.length > 0 || isSubmitting) {
                        const tooltip = document.getElementById('step1-tooltip');
                        tooltip.style.opacity = '1';
                      }
                    }}
                    onMouseLeave={() => {
                      const missingFields = getStep1MissingFields();
                      const tooltip = document.getElementById('step1-tooltip');
                      if (missingFields.length > 0 || isSubmitting) {
                        tooltip.style.opacity = '0';
                      }
                    }}
                      onClick={(e) => {
                        const missingFields = getStep1MissingFields();
                        if (missingFields.length > 0) {
                          // Show tooltip briefly when button is clicked with missing fields
                          e.preventDefault();
                          const tooltip = document.getElementById('step1-tooltip');
                          if (tooltip) {
                            tooltip.style.opacity = '1';
                            setTimeout(() => {
                              tooltip.style.opacity = '';
                            }, 2000);
                          }
                        } else {
                          handleNext();
                        }
                      }}
                      className={`w-full px-6 py-4 rounded-lg transition-colors font-medium group touch-manipulation ${
                        getStep1MissingFields().length > 0
                          ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                          : "bg-gray-900 text-white hover:bg-gray-800 cursor-pointer"
                      }`}

                    >
                      <div className="flex items-center justify-center space-x-2">
                        <span>Continue to Contact Details</span>
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </button>
                    
                    {getStep1MissingFields().length > 0 && (
                      <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10" id="step1-tooltip">
                        Required: {getStep1MissingFields().join(", ")}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className={`p-4 sm:p-8 ${!isTransitioning ? 'animate-slideIn' : ''}`}>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-medium text-gray-900">
                      Contact & Shipping Information
                    </h2>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Back</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Full Name
                          </label>
                          {formData.name && (
                            <CheckCircle className="w-4 h-4 text-teal-500 ml-2" />
                          )}
                        </div>
                        <input
                          type="text"
                          name="fullName"
                          autoComplete="name"
                          autoFocus={currentStep === 2 && window.innerWidth >= 768}
                          value={formData.name}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className={`w-full px-3 py-3 border focus:border-blue-500 focus:outline-none transition-colors rounded-lg ${
                            formData.name
                              ? "border-teal-500"
                              : "border-gray-300"
                          }`}
                          placeholder="John Smith"
                          required
                        />
                      </div>
                      <div>
                        <div className="flex items-center mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Email Address
                          </label>
                          {formData.email && emailValid && (
                            <CheckCircle className="w-4 h-4 text-teal-500 ml-2" />
                          )}
                          {!emailValid && (
                            <p className="text-red-400 text-xs ml-2">
                              Please enter a valid email address
                            </p>
                          )}  
                        </div>
                        <input
                          type="email"
                          name="email"
                          autoComplete="email"
                          inputMode="email"
                          value={formData.email}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          className={`w-full px-3 py-3 border focus:outline-none transition-colors rounded-lg ${
                            !emailValid
                              ? "border-red-500 focus:border-red-500 bg-red-50"
                              : formData.email && emailValid
                              ? "border-teal-500 focus:border-blue-500"
                              : "border-blue-300 focus:border-blue-500"
                          }`}
                          placeholder="john@company.com"
                          required
                        />
                      
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Company{" "}
                          <span className="text-gray-400">(Optional)</span>
                        </label>
                        {formData.company && (
                          <CheckCircle className="w-4 h-4 text-teal-500 ml-2" />
                        )}
                      </div>
                      <input
                        type="text"
                        name="company"
                        autoComplete="organization"
                        value={formData.company}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            company: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-3 border focus:border-blue-500 focus:outline-none transition-colors rounded-lg ${
                          formData.company
                            ? "border-teal-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Acme Corporation"
                      />
                    </div>

                    {!skipShipping && (
                      <div className="relative">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <label className="text-sm font-medium text-gray-700 mr-2">
                              Shipping Address
                            </label>
                            {formData.shipping && formData.shipping.length > 10 && (
                              <CheckCircle className="w-4 h-4 text-teal-500" />
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="skipShipping"
                              checked={skipShipping}
                              onChange={(e) => {
                                const isSkipping = e.target.checked;
                                setSkipShipping(isSkipping);
                                
                                if (isSkipping) {
                                  // Preserve current shipping data before hiding it
                                  setPreservedShippingData({
                                    shipping: formData.shipping,
                                    receiving: formData.receiving
                                  });
                                  setFormData((prev) => ({
                                    ...prev,
                                    skipShipping: true,
                                  }));
                                  setAddressSelected(false);
                                } else {
                                  // Restore preserved shipping data
                                  setFormData((prev) => ({
                                    ...prev,
                                    skipShipping: false,
                                    shipping: preservedShippingData.shipping,
                                    receiving: preservedShippingData.receiving,
                                  }));
                                  
                                  // If there's preserved shipping data, restore addressSelected
                                  if (preservedShippingData.shipping && preservedShippingData.shipping.length > 10) {
                                    setAddressSelected(true);
                                  }
                                }
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <label
                              htmlFor="skipShipping"
                              className="text-sm text-gray-600 cursor-pointer whitespace-wrap"
                            >
                              Skip for now
                            </label>
                          </div>
                        </div>
                      <div className="relative">
                        <input
                          type="text"
                          name="address"
                          autoComplete="address-line1"
                          value={formData.shipping}
                          onChange={(e) => handleShippingChange(e.target.value)}
                          onBlur={() => {
                            if (formData.shipping && formData.shipping.length > 10) {
                              setAddressSelected(true);
                            }
                          }}
                          className={`w-full px-3 py-3 border focus:outline-none transition-colors pl-12 rounded-lg ${
                            formData.shipping
                              ? "border-teal-500 focus:border-teal-600"
                              : "border-gray-300 focus:border-blue-500"
                          }`}
                          placeholder="Start typing..."
                          required
                        />
                        <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />

                        {showSuggestions && shippingSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-48 overflow-y-auto">
                            {shippingSuggestions.map((suggestion, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    shipping: suggestion.name || suggestion,
                                  }));
                                  setShowSuggestions(false);
                                  setAddressSelected(true);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm rounded-lg"
                              >
                                {suggestion.name || suggestion}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {!skipShipping && isLargeProject() && addressSelected && (
                      <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 overflow-hidden ${shippingBoxAnimating ? 'animate-slideDown' : ''}`}>
                        <div className="flex items-center space-x-3 mb-4" >
                          <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                            <Truck className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">
                              Large Quantity Shipping
                            </h4>
                            <p className="text-sm text-gray-600">
                              {getShippingThresholdMessage()}
                            </p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Select Receiving Method
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {receivingOptions.map((option) => {
                            const Icon = option.icon;
                            return (
                              <label
                                key={option.id}
                                className="cursor-pointer block"
                              >
                                <input
                                  type="radio"
                                  name="receiving"
                                  value={option.id}
                                  checked={formData.receiving === option.id}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      receiving: e.target.value,
                                    }))
                                  }
                                  className="sr-only"
                                />
                                <div
                                  className={`p-3 border rounded-md transition-colors ${
                                    formData.receiving === option.id
                                      ? "border-blue-400 bg-blue-50"
                                      : "border-gray-200 hover:border-blue-300 bg-white"
                                  } rounded-lg`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className={`p-1.5 rounded ${
                                        formData.receiving === option.id
                                          ? "bg-blue-500"
                                          : "bg-gray-100"
                                      }`}
                                    >
                                      <Icon
                                        className={`w-4 h-4 ${
                                          formData.receiving === option.id
                                            ? "text-white"
                                            : "text-gray-600"
                                        }`}
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <h5 className="font-medium text-gray-900">
                                        {option.name}
                                      </h5>
                                      <p className="text-sm text-gray-600">
                                        {option.desc}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {option.suitable}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                          </div>
                        </div>
                      </div>
                    )}

                    {skipShipping && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                              <Info className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                Shipping Details Skipped
                              </h4>
                              <p className="text-sm text-gray-600">
                                We'll contact you to arrange shipping and delivery details after reviewing your quote.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <input
                              type="checkbox"
                              id="unSkipShipping"
                              checked={!skipShipping}
                              onChange={(e) => {
                                const isNotSkipping = e.target.checked;
                                setSkipShipping(!isNotSkipping);
                                
                                if (!isNotSkipping) {
                                  // Preserve current shipping data before hiding it
                                  setPreservedShippingData({
                                    shipping: formData.shipping,
                                    receiving: formData.receiving
                                  });
                                  setFormData((prev) => ({
                                    ...prev,
                                    skipShipping: true,
                                  }));
                                  setAddressSelected(false);
                                } else {
                                  // Restore preserved shipping data
                                  setFormData((prev) => ({
                                    ...prev,
                                    skipShipping: false,
                                    shipping: preservedShippingData.shipping,
                                    receiving: preservedShippingData.receiving,
                                  }));
                                  
                                  // If there's preserved shipping data, restore addressSelected
                                  if (preservedShippingData.shipping && preservedShippingData.shipping.length > 10) {
                                    setAddressSelected(true);
                                  }
                                }
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <label
                              htmlFor="unSkipShipping"
                              className="text-sm text-gray-700 cursor-pointer whitespace-nowrap"
                            >
                              Provide now
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {!showNotes ? (
                      <button
                        type="button"
                        onClick={() => setShowNotes(true)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-2"
                      >
                        <span>+ Add project notes (optional)</span>
                      </button>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Additional Notes{" "}
                            <span className="text-gray-400">(Optional)</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setShowNotes(false);
                              setFormData((prev) => ({ ...prev, notes: "" }));
                            }}
                            className="text-gray-400 hover:text-gray-600 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <textarea
                          value={formData.notes || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 border border-gray-300 focus:border-blue-500 focus:outline-none transition-colors resize-none rounded-lg"
                          rows={3}
                          placeholder="Timeline requirements, special installation conditions, quantity expectations, or other project details..."
                        />
                      </div>
                    )}

                    <div className="flex items-start space-x-3 mb-4">
                      <input
                        type="checkbox"
                        id="mailingList"
                        checked={formData.mailingList}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            mailingList: e.target.checked,
                          }))
                        }
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <label
                        htmlFor="mailingList"
                        className="text-sm text-gray-700 leading-relaxed"
                      >
                        Subscribe to our mailing list for product updates, industry insights, and exclusive offers
                      </label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <label
                        htmlFor="terms"
                        className="text-sm text-gray-700 leading-relaxed"
                      >
                        I agree to the{" "}
                        <a
                          href="https://coldrays.com/terms-conditions/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Terms and Conditions
                        </a>{" "}
                        and{" "}
                        <a
                          href="https://coldrays.com/privacy-policy/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>

                    {submitError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left">
                        <h4 className="font-medium text-red-900 mb-2">
                          {submitError.title}
                        </h4>
                        <p className="text-sm text-red-700 leading-relaxed">
                          {submitError.message}
                        </p>
                        {submitError.details && (
                          <p className="text-xs text-red-500 mt-2">
                            Technical details: {submitError.details}
                          </p>
                        )}
                        <div className="mt-3 flex items-center space-x-3">
                          <button
                            onClick={() => setSubmitError(null)}
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            Dismiss
                          </button>
                          <span className="text-red-300">|</span>
                          <a
                            href="mailto:info@coldrays.com"
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            Contact Support
                          </a>
                        </div>
                      </div>
                    )}

                    <div className="relative pb-8 sm:pb-2">
                      <button
                        onMouseEnter={() =>  {
                          const missingFields = getStep2MissingFields();
                          if (missingFields.length > 0 || isSubmitting) {
                            const tooltip = document.getElementById('step2-tooltip');
                            tooltip.style.opacity = '1';
                          }
                        }}
                        onMouseLeave={() => {
                          const missingFields = getStep2MissingFields();
                          const tooltip = document.getElementById('step2-tooltip');
                          if (missingFields.length > 0 || isSubmitting) {
                            tooltip.style.opacity = '0';
                          }
                        }}
                        onClick={(e) => {
                          const missingFields = getStep2MissingFields();
                          if (missingFields.length > 0 || isSubmitting) {
                            // Show tooltip briefly when button is clicked with missing fields
                            if (!isSubmitting) {
                              e.preventDefault();
                              const tooltip = document.getElementById('step2-tooltip');
                              if (tooltip) {
                                tooltip.style.opacity = '1';
                                setTimeout(() => {
                                  tooltip.style.opacity = '';
                                }, 2000);
                              }
                            }
                          } else {
                            handleSubmit();
                          }
                        }}
                        className={`w-full px-6 py-4 transition-colors font-medium min-h-[56px] touch-manipulation group rounded-lg ${
                          getStep2MissingFields().length > 0 || isSubmitting
                            ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                            : "bg-gray-900 text-white hover:bg-gray-800 cursor-pointer"
                        }`}

                      >
                        {isSubmitting ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center space-x-2">
                            <Send className="w-5 h-5" />
                            <span>Submit Quote Request</span>
                          </div>
                        )}
                      </button>
                      
                      {getStep2MissingFields().length > 0 && !isSubmitting && (
                        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10" id="step2-tooltip">
                          Required: {getStep2MissingFields().join(", ")}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-8 bg-white/40 backdrop-blur-sm border border-gray-200/30 rounded-2xl p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Quote Summary
              </h3>

              <div className="mb-6 p-2 bg-gradient-to-r from-orange-50 to-blue-50 rounded-lg border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Heat Level
                  </span>
                  <span className="text-xs text-gray-500">
                    {progressPercentage}% Complete
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-3 bg-gradient-to-r from-orange-400 via-yellow-400 to-blue-400 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Hot</span>
                  <span>Cool</span>
                </div>
              </div>

              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Desired Coverage:</span>
                  <span className="font-medium text-right">
                    {formData.coverage
                      ? `${formatNumber(formData.coverage)} ${getUnitLabel()}`
                      : "—"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Product Type:</span>
                  <span className="font-medium text-right">
                    {getSelectedProduct()?.name || "—"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Specific Product:</span>
                  <span className="font-medium text-right max-w-[150px]">
                    {(() => {
                      if (formData.product && formData.subProduct && subProducts[formData.product]) {
                        const subProduct = subProducts[formData.product].find(opt => opt.id === formData.subProduct);
                        return subProduct?.name || formData.subProduct;
                      }
                      return formData.subProduct;
                    })()}
                  </span>
                </div>

                {getUnitsNeeded() && formData.product !== 'liquid-applied' && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Rolls:</span>
                    <span className="font-medium">
                      {getUnitsNeeded()?.unitsDisplay} {(() => {
                        const maxCount = getUnitsNeeded()?.unitsMax || 1;
                        const minCount = getUnitsNeeded()?.unitsMin || 1;
                        const isSingle = maxCount === 1 && minCount === 1;
                        return getUnitTerm(isSingle ? 1 : 2);
                      })()}
                    </span>
                  </div>
                )}

                {getUnitsNeeded() && formData.product !== 'liquid-applied' && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Coverage:</span>
                    <span className="font-medium">
                      {getUnitsNeeded()?.totalCoverageDisplay}
                    </span>
                  </div>
                )}

                {currentStep === 2 && formData.product === "not-sure" && formData.application && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-gray-600 block mb-1">
                      Application Notes:
                    </span>
                    <span className="text-xs text-gray-500 leading-relaxed">
                      {formData.application.slice(0, 100)}
                    </span>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-600">Contact:</span>
                      <span className="font-medium">
                        {formData.name || "—"}
                      </span>
                    </div>

                    {!skipShipping && (
                      <>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">Ship to:</span>
                          <span className="font-medium text-right max-w-[120px] truncate">
                            {formData.shipping || "—"}
                          </span>
                        </div>
                        {isLargeProject() && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Receiving:</span>
                            <span className="font-medium text-right max-w-[120px] truncate">
                              {formData.receiving
                                ? receivingOptions.find(
                                    (r) => r.id === formData.receiving
                                  )?.name || "—"
                                : "—"}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {skipShipping && (
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">Shipping:</span>
                        <span className="font-medium">
                          Details to be arranged
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* MOQ Warning for Desktop */}
              {isBelowMOQ() && (
                <div className="mt-4 p-2 py-1.5 bg-orange-50 border-l-4 border-orange-200 text-xs">
                  <div className="flex items-start space-x-1">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-orange-800">
                          ⓘ Below Minimum Order Quantity
                        </p>
                        <button
                          onClick={() => setShowMOQHelp(!showMOQHelp)}
                          className="flex items-center space-x-1 text-xs bg-orange-50 hover:bg-orange-200 text-orange-700 px-2 py-0.5 rounded transition-colors -mt-1"
                        >
                          <span>{showMOQHelp ? 'Hide' : 'Info'}</span>
                          {showMOQHelp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      </div>
                      <div className="text-orange-700 leading-relaxed">
                        {getMOQWarning()?.message}
                      </div>
                      {getMOQWarning()?.retailMessage && (
                        <div className="text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{__html: getMOQWarning()?.retailMessage}}>
                        </div>
                      )}
                      {showMOQHelp && (
                        <div className="text-gray-600 text-xs leading-relaxed animate-fadeIn">
                          <strong>Note:</strong> Quotes below MOQ may not be able to be fulfilled through our standard wholesale process, but we're still happy to review your request and explore custom solutions or retail options.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 leading-relaxed">
                  We'll provide detailed pricing, delivery timelines, and installation guides within 1-2 business days.
                </p>
              </div>

              {formData.product && formData.coverage && (
                <div className="mt-2 p-2 py-1.5 bg-amber-50 border-l-4 border-amber-200 text-xs">
                  <div className="text-gray-600 leading-relaxed">
                    Quoted coverage assumes smooth, flat surface.
                    Actual requirements vary due to{" "}
                    {formData.product === "liquid-applied" &&
                      "installation method (sprayed or rolled), surface texture, surface shape, and waste factor"}
                    {formData.product === "fabric" &&
                      "seams and material layout"}
                    {formData.product === "self-adhesive" &&
                      "surface shape and waste factor"}
                    {formData.product === "window-tint" &&
                      "cutting requirements"}
                    {formData.product === "not-sure" &&
                      "installation method and surface conditions"}
                    . Add{" "}
                    {formData.product === "liquid-applied" ||
                    formData.product === "self-adhesive"
                      ? "10% for simple installations, 25% for complex shapes like corrugated roofing"
                      : "10% for waste and installation variables"}
                    .
                    {formData.product === "self-adhesive" && " Depending on layout, adjacent pieces may require overlap of up to 8%."}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadiativeCoolingQuote;
